// Derived scan-geometry types — the TypeScript port of iOS `ScanGeometry`.
//
// RoomPlan conventions (ground truth, see CLAUDE.md / fixtures):
//   • Units are meters, the floor plane is world X–Z, Y is up.
//   • A surface `transform` is a FLAT array of 16 floats, COLUMN-MAJOR: four
//     columns of four. Column 0 = local X axis, column 1 = local Y (up),
//     column 2 = local Z, column 3 = translation. Floor-plane position is
//     (t[12], t[14]); height y is t[13].
//   • `dimensions` for surfaces = [width(localX), height(localY), 0]; for
//     objects = [width(localX), height(localY), depth(localZ)].
//
// `Vec2` holds an (x, z) point on the floor plane: `Vec2.y` carries world-Z,
// matching the iOS `SIMD2<Float>(x, z)` convention used throughout.

/** A surface (wall / door / window / opening / floor) decoded from room.json. */
export interface Surface {
  /** 16 column-major floats. */
  transform: number[];
  /** [width(localX), height(localY), 0] for surfaces. */
  dimensions: number[];
  /** LOCAL-frame corners [[x,y,z], ...]; walls & floors carry these, openings are empty. */
  polygonCorners: number[][];
  /** {"wall":{}} | {"door":{"isOpen":false}} | ... or a plain string. */
  category: any;
  identifier?: string;
  parentIdentifier?: string | null;
  curve?: any;
  story?: number;
  confidence?: any;
  completedEdges?: any;
}

/** A detected object (furniture/appliance) decoded from room.json. */
export interface RoomObject {
  /** 16 column-major floats. */
  transform: number[];
  /** [width(localX), height(localY), depth(localZ)]. */
  dimensions: number[];
  /** {"storage":{}} | {"chair":{}} | {"stairs":{}} | ... or a plain string. */
  category: any;
  attributes?: any;
  identifier?: string;
  parentIdentifier?: string | null;
  story?: number;
  confidence?: any;
}

/** Top-level room.json shape. */
export interface CapturedRoomJSON {
  version?: number;
  coreModel?: string;
  story?: number;
  referenceOriginTransform?: number[];
  sections?: any[];
  walls: Surface[];
  doors: Surface[];
  windows: Surface[];
  openings: Surface[];
  floors: Surface[];
  objects: RoomObject[];
}

/** A 2D point on the floor plane. `y` holds world-Z (matches iOS SIMD2(x, z)). */
export interface Vec2 {
  x: number;
  y: number;
}

/** A wall segment: its two endpoints on the floor plane + height. */
export interface WallSegment {
  id: string;
  /** Stable RoomPlan surface UUID (used to host openings by parentIdentifier). */
  surfaceID?: string;
  start: Vec2;
  end: Vec2;
  height: number;
}

export type OpeningKind = "door" | "window" | "opening";

/** A door / window / cased-opening projected to the floor plane. */
export interface OpeningSegment {
  id: string;
  kind: OpeningKind;
  surfaceID?: string;
  start: Vec2;
  end: Vec2;
  width: number;
  height: number;
  /** RoomPlan parentIdentifier — the wall surface this opening sits in. */
  parentWallId?: string | null;
  /** World y of the opening center (sill/head reference). */
  centerY: number;
}

/** A furniture/appliance footprint on the floor plane. */
export interface ObjectFootprint {
  id: string;
  label: string;
  /** Four floor-plane corners in polygon order [BL, BR, TR, TL]. */
  corners: Vec2[];
  /** [width, height, depth] meters. */
  dimensions: number[];
  center: Vec2;
  isStairs: boolean;
  /** Original 16-float transform (3D scene rebuilds the box from this). */
  transform: number[];
}

/** An opening projected onto its host wall's centerline. */
export interface WallOpeningSlot {
  id: number;
  opening: OpeningSegment;
  /** Arc length from the wall start to the opening's near edge. */
  u0: number;
  /** Arc length to the far edge. */
  u1: number;
  startPoint: Vec2;
  endPoint: Vec2;
}

/** A solid stretch of wall between gaps, [s, e] in arc length. */
export interface WallRun {
  id: number;
  s: number;
  e: number;
  startPoint: Vec2;
  endPoint: Vec2;
}

/** A wall split into solid runs + opening gaps, with the sorted breakpoint chain. */
export interface WallDecomposition {
  id: string; // == WallSegment.id
  wall: WallSegment;
  runs: WallRun[];
  slots: WallOpeningSlot[];
  /** Sorted, collapsed breakpoints: {0, L} ∪ all slot u0/u1. */
  breakpoints: number[];
}

/** A solid wall run thickened into a (mitered) plan-view polygon. */
export interface ThickWall {
  id: string; // `${wallID}:${runIndex}`
  wallID: string;
  polygon: Vec2[];
}

/** Synthesized wall thickness (RoomPlan reports none). Meters. */
export interface WallThickness {
  /** 4.5″ interior (2x4 + drywall). */
  interior: number;
  /** 6.5″ exterior (2x6). */
  exterior: number;
}

export const DEFAULT_WALL_THICKNESS: WallThickness = {
  interior: 0.1143,
  exterior: 0.1651,
};

/** Everything the floorplan + 3D scene need, derived once from a CapturedRoom. */
export interface ScanGeometry {
  walls: WallSegment[];
  doors: OpeningSegment[];
  windows: OpeningSegment[];
  openings: OpeningSegment[];
  objects: ObjectFootprint[];
  /** Room outline on the floor plane (world x/z). */
  floorPolygon: Vec2[];
  boundsMin: Vec2;
  boundsMax: Vec2;
  floorAreaSquareMeters: number;
  ceilingHeightMeters: number;
  perimeterMeters: number;
  /** World y of the floor plane (datum for above-finished-floor heights). */
  floorY: number;
  /** Per-wall split into solid runs + opening gaps. */
  wallDecompositions: WallDecomposition[];
  /** Solid wall runs thickened into mitered plan polygons (default thickness). */
  thickWalls: ThickWall[];
  raw: CapturedRoomJSON;
}
